<template>
  <div class="flex md:items-center justify-center h-screen w-full">
    <div class="xxs:max-w-[320px] lg:max-w-[360px] mx-auto md:h-fit h-full relative rounded-[20px] background-shadow flex flex-col items-center justify-center w-full">
        <div v-if="organization" class="flex flex-col justify-start items-start mb-6 w-full">
            <img v-if="organization.theme_settings && organization.theme_settings.logo"
                 :src="useImagePathFormatter(organization.slug, organization.theme_settings.logo)"
                 class="object-contain w-20 h-20 rounded-full mb-4"
                 alt="logo"
            />
            <DefaultOrgIcon v-else/>
            <h2 class="text-left xxs:text-body-2 lg:text-body-1 font-medium text-white">
                Welcome to {{ organization.name }}!
            </h2>
        </div>
        <div v-else class="flex flex-row justify-between items-center mb-6 w-full">
            <h2 class="text-left lg:text-body-1 md:text-body-2 text-body-3 font-medium text-white">
                Welcome to Connect Plus+
            </h2>
            <DefaultOrgIcon/>
        </div>

      <div class="w-full">
        <p class="text-body-3 text-white font-medium">Basic Info</p>

        <div class="my-6">
          <div class="mb-6">
            <core-input
              name="first_name"
              label="First Name"
              label-position="above"
              placeholder="First Name"
              v-model="firstName.value.value"
              :has-error="firstName.errorMessage.value !== undefined"
              :hint-text="firstName.errorMessage.value"
              :colorMode="colorMode"
            >
            </core-input>
          </div>

          <div class="mb-6">
            <core-input
              name="last_name"
              label="Last Name"
              label-position="above"
              placeholder="Last Name"
              v-model="lastName.value.value"
              :has-error="lastName.errorMessage.value !== undefined"
              :hint-text="lastName.errorMessage.value"
              :colorMode="colorMode"
            >
            </core-input>
          </div>

          <div class="mb-6">
            <phone-input
              label="Mobile Number"
              :region-code="phoneNumberRegion"
              v-model:value="phoneNumber.value.value"
              :disabled="invitationStore.invitation.phone_number"
              :colorMode="colorMode"
            />
          </div>
        </div>

        <div class="mt-4 flex flex-row">
          <CoreCheckbox
            v-model="terms.value.value"
            color="#556EE6"
            class="mr-2"
          />
          <p class="text-body-5 font-normal text-default-3">
            I agree to the
            <a class="text-primary-1" href="https://www.nepservices.com/legal/terms-of-service" target="_blank">Terms and Conditions</a>.
          </p>
        </div>

        <div class="mt-10">
          <CoreButton
            :disabled="!meta.valid"
            button-type="primary"
            @click="handleOnVerify"
            label="Send verification code"
            :loading="buttonLoader"
            :color-mode="colorMode"
          />
        </div>
      </div>
    </div>
  </div>
  <Modal
    :show="showVerificationModal"
    @update:show="showVerificationModal = !showVerificationModal"
    @close="handleCanceled"
    :colorMode="colorMode"
  >
        <VerifyPhoneNumber
                v-if="invitationStore.invitation.uid"
                :show="showVerificationModal"
                :phone-number="phoneNumber.value.value"
                :uid="invitationStore.invitation.uid"
                @canceled="handleCanceled"
                @finished="handleFinished"
                type="invitations"
                :colorMode="colorMode"
        />
  </Modal>
</template>

<script setup>
import CoreCheckbox from "@/components/core/Checkbox.vue";
import CoreButton from "@/components/core/Button.vue";
import CoreInput from "@/components/core/InputGroup";
import PhoneInput from "@/components/inputs/PhoneInput.vue";
import VerifyPhoneNumber from "@/components/modals/partials/VerifyPhoneNumber.vue";

import { useField, useForm } from "vee-validate";
import { onBeforeMount, onMounted, ref } from "vue";
import { useInvitation } from "@/stores/InvitationStore";
import { useVerifyPhoneNumber } from "@/stores/VerifyPhoneNumberStore";
import { phone } from "@/yup/validations";
import * as yup from "yup";
import { useRoute, useRouter } from "vue-router";
import {storeToRefs} from "pinia";
import {useLocale} from "@/stores/LocaleStore";
import Modal from "@/components/core/Modal.vue";
import {useAuth} from "@/stores/AuthStore";
import {useImagePathFormatter} from "@/composables/useImagePathFormatter";
import DefaultOrgIcon from "@/components/icons/small/DefaultOrgIcon.vue";
import {toast} from "vue3-toastify";
import {useSignUp} from "@/stores/SignUpStore";
import {useLogout} from "@/composables/useLogout";

const {organization} = storeToRefs(useAuth());

const invitationStore = useInvitation();
const verifyPhoneNumberStore = useVerifyPhoneNumber();
const route = useRoute();
const {phoneNumberRegion} = storeToRefs(useLocale());
const showVerificationModal = ref(false);

const colorMode = 'dark'

const buttonLoader = ref(false);

const { handleLogout } = useLogout();

onBeforeMount(async () => {
    const router = useRouter();

    if (!route.query.token) {
        router.push({ path: "/" });
    }

    const auth = useAuth();
    const signUp = useSignUp();

    if (auth.isAuthenticated && !signUp.inProgress && auth.member) {
        await handleLogout(false);
    }

    invitationStore
        .getInvitation(route.params.uid, route.query.token)
        .then(() => {
            firstName.value.value = invitationStore.invitation.first_name;
            lastName.value.value = invitationStore.invitation.last_name;
            phoneNumber.value.value = invitationStore.invitation.phone_number ?? "";
        })
        .catch(() => {
            router.push({ path: "/" }).then(() => {
                toast.error("This invitation has already been accepted.");
            })
        });
});

onMounted(() => {
    verifyPhoneNumberStore.setType("invitations");
});

yup.addMethod(yup.StringSchema, "phone", phone);

const { meta } = useForm({
    initialValues: {
        first_name: "",
        last_name: "",
        phone_number: "",
    },
});

const firstName = useField(
    "first_name",
    yup
        .string()
        .required()
        .matches(/^[A-Za-z-'" ]*$/, "Please enter a valid name")
        .label("The first name")
);
const lastName = useField(
    "last_name",
    yup
        .string()
        .required()
        .matches(/^[A-Za-z-'" ]*$/, "Please enter a valid name")
        .label("The last name")
);
const phoneNumber = useField(
    "phone_number",
    yup.string().phone(phoneNumberRegion.value, true).label("The phone number")
);
const terms = useField("terms", yup.boolean().required().oneOf([true]));
const handleOnVerify = () => {
    if (!meta.value.valid) {
        return;
    }

    buttonLoader.value = true;

    if (invitationStore.invitation.phone_number !== null) {
        verifyPhoneNumberStore
            .sendOTP(route.params.uid, phoneNumber.value.value, route.query.token)
            .then(() => {
                showVerificationModal.value = true;
                buttonLoader.value = false;
            });
        return;
    }

    invitationStore
        .updateInvitation(route.params.uid, route.query.token, {
            first_name: firstName.value.value,
            last_name: lastName.value.value,
            phone_number: phoneNumber.value.value,
        })
        .then(() => {
            verifyPhoneNumberStore
                .sendOTP(route.params.uid, phoneNumber.value.value, route.query.token)
                .then(() => {
                    showVerificationModal.value = true;
                    buttonLoader.value = false;
                });
        });
};
const handleFinished = () => {
    buttonLoader.value = false;
    showVerificationModal.value = false;
};

const handleCanceled = () => {
    buttonLoader.value = false;
    showVerificationModal.value = false;
};
</script>
